import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2, X } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { apiClient, getApiErrorMessage } from "../../../lib/apiClient";

// Automatic Route Planning (plan.md #1) — pick drivers running today +
// confirm/edit a suggested tiffin target and time-per-stop each, then
// generate all their routes at once onto the same builder screen for
// review/optimize/finalize. Rendered inline next to the map (not a modal)
// so the map stays fully visible — every checked driver's home endpoint is
// previewed live on the map (via onPreviewChange) so the manager can swap to
// a different driver before committing to one.
export function AutoPlanPanel({ date, onGenerated, driversFull, usedDriverIds, onPreviewChange, onClose, suggestions, loading }) {
  const [generating, setGenerating] = useState(false);
  const [drivers, setDrivers] = useState([]);

  const driverInfo = useMemo(() => new Map((driversFull || []).map((d) => [d.id, d])), [driversFull]);
  const usedSet = useMemo(() => new Set(usedDriverIds || []), [usedDriverIds]);

  useEffect(() => {
    setDrivers((suggestions || []).map((d) => ({
      ...d, selected: !usedSet.has(d.driver_id), count: d.suggested_count, serviceTime: d.suggested_service_time_minutes,
      maxDistanceKm: d.suggested_max_distance_km ?? "",
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions]);

  useEffect(() => {
    if (!onPreviewChange) return;
    const preview = drivers.filter((d) => d.selected).map((d) => {
      const full = driverInfo.get(d.driver_id);
      if (!full?.latitude || !full?.longitude) return null;
      return { driverId: d.driver_id, name: d.driver_name, address: d.driver_address, latitude: full.latitude, longitude: full.longitude, color: full.color };
    }).filter(Boolean);
    onPreviewChange(preview);
    return () => onPreviewChange([]);
  }, [drivers, driverInfo, onPreviewChange]);

  const toggle = (id) => setDrivers((rs) => rs.map((d) => (d.driver_id === id ? { ...d, selected: !d.selected } : d)));
  const setCount = (id, v) => setDrivers((rs) => rs.map((d) => (d.driver_id === id ? { ...d, count: v } : d)));
  const setServiceTime = (id, v) => setDrivers((rs) => rs.map((d) => (d.driver_id === id ? { ...d, serviceTime: v } : d)));
  const setMaxDistance = (id, v) => setDrivers((rs) => rs.map((d) => (d.driver_id === id ? { ...d, maxDistanceKm: v } : d)));

  const selected = drivers.filter((d) => d.selected && Number(d.count) > 0);

  const handleGenerate = async () => {
    if (selected.length === 0) { toast.error("Select at least one driver."); return; }
    setGenerating(true);
    try {
      const { data } = await apiClient.post("/routes/auto-plan", {
        date, drivers: selected.map((d) => ({
          driver_id: d.driver_id, target_count: Number(d.count), service_time_minutes: Number(d.serviceTime) || 0,
        })),
      });
      const serviceTimeByDriver = {};
      const maxDistanceByDriver = {};
      selected.forEach((d) => {
        serviceTimeByDriver[d.driver_id] = Number(d.serviceTime) || 0;
        maxDistanceByDriver[d.driver_id] = d.maxDistanceKm === "" ? "" : Number(d.maxDistanceKm);
      });
      const created = onGenerated(data, serviceTimeByDriver, maxDistanceByDriver);
      if (created) onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not generate routes."));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-surface p-4" data-testid="auto-plan-panel">
      <div className="mb-2 flex items-center justify-between">
        <p className="font-heading text-sm font-semibold text-text-main">Plan Routes Automatically</p>
        <Button variant="ghost" size="icon" onClick={onClose} disabled={generating} data-testid="auto-plan-close-button">
          <X size={15} strokeWidth={1.5} />
        </Button>
      </div>
      <p className="mb-3 text-xs text-text-muted">
        Sorted by most recently active first. Tiffin count, time-per-stop, and which customers get picked are all copied from each driver's own most recent route — not an average — with the count +2. Customers who aren't due today are swapped for the nearest other due-today stop in that same area. Check a driver to preview their home endpoint on the map (dashed circle) before committing.
      </p>

      {loading ? (
        <p className="py-6 text-center text-sm text-text-muted">Loading driver history...</p>
      ) : drivers.length === 0 ? (
        <p className="py-6 text-center text-sm text-text-muted" data-testid="auto-plan-no-drivers">
          Every active driver already has a route for {date}, or there are no available drivers.
        </p>
      ) : (
        <div className="max-h-[360px] space-y-2 overflow-y-auto" data-testid="auto-plan-driver-list">
          {drivers.map((d) => {
            const isUsed = usedSet.has(d.driver_id);
            return (
            <div key={d.driver_id} className={`rounded-md border border-border p-2.5 ${isUsed ? "opacity-50" : ""}`} data-testid={`auto-plan-driver-row-${d.driver_id}`}>
              <div className="flex items-start gap-2">
                <Checkbox checked={d.selected} disabled={isUsed} onCheckedChange={() => toggle(d.driver_id)} className="mt-0.5" data-testid={`auto-plan-driver-checkbox-${d.driver_id}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-text-main">{d.driver_name || `Driver #${d.driver_id}`}</p>
                  <p className="truncate text-[11px] text-text-muted" data-testid={`auto-plan-driver-address-${d.driver_id}`}>{d.driver_address || "No address on file"}</p>
                  <p className="text-[11px] text-text-muted">
                    {isUsed ? "Already has a draft route on this screen" : d.is_new_driver ? "New driver — using fleet average" : `Highest of last 2 worked days (${d.avg_tiffins}) + 5`}
                    {!isUsed && (d.last_route_date ? ` · last ran ${d.last_route_date}` : " · never run a route")}
                  </p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-3 pl-6">
                <div className="flex flex-col items-center gap-0.5">
                  <Input
                    type="number" min="1" className="w-16" value={d.count} disabled={!d.selected}
                    onChange={(e) => setCount(d.driver_id, e.target.value)}
                    data-testid={`auto-plan-count-input-${d.driver_id}`}
                  />
                  <span className="text-[10px] text-text-muted">tiffins</span>
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <Input
                    type="number" min="0" max="60" step="0.5" className="w-16" value={d.serviceTime} disabled={!d.selected}
                    onChange={(e) => setServiceTime(d.driver_id, e.target.value)}
                    data-testid={`auto-plan-service-time-input-${d.driver_id}`}
                  />
                  <span className="text-[10px] text-text-muted" data-testid={`auto-plan-service-time-label-${d.driver_id}`}>
                    min/stop{d.service_time_is_saved ? " · saved" : ""}
                  </span>
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <Input
                    type="number" min="0" step="1" className="w-20 px-1.5 text-center" value={d.maxDistanceKm} disabled={!d.selected}
                    placeholder="—" onChange={(e) => setMaxDistance(d.driver_id, e.target.value)}
                    data-testid={`auto-plan-max-distance-input-${d.driver_id}`}
                  />
                  <span className="text-[10px] text-text-muted">km max</span>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Button
        variant="accent" className="mt-3 w-full" disabled={generating || selected.length === 0} onClick={handleGenerate}
        data-testid="auto-plan-generate-button"
      >
        {generating ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} strokeWidth={1.5} />} {generating ? "Generating..." : `Generate ${selected.length} Route(s)`}
      </Button>
    </div>
  );
}
